import { useState, useEffect, useCallback } from 'react';
import { Text, Dropdown, IDropdownOption, Spinner, SpinnerSize, MessageBar, MessageBarType, ActionButton, IconButton, TooltipHost } from '@fluentui/react';
import type { EnvironmentConfig, MissionControlEnvironment, EnvironmentType, AccessLevel } from '../../types/environment';
import { ENV_TYPE_ORDER, ACCESS_LEVEL_LABELS, canWrite, groupEnvironmentsByType } from '../../types/environment';
import { fetchEnvironmentsByWorkspace, deleteDevBox as deleteDevBoxApi } from '../../services/environments';
import ClaimDevBoxDialog from './ClaimDevBoxDialog';
import ExtendDevBoxDialog from './ExtendDevBoxDialog';
import ShareDevBoxDialog from './ShareDevBoxDialog';
import styles from './EnvironmentPicker.module.css';

export interface EnvironmentPickerProps {
  workspaceCode: string | undefined;
  value: EnvironmentConfig[];
  onChange: (configs: EnvironmentConfig[]) => void;
  readOnly?: boolean;
  userEmail: string;
  /** Show lifecycle actions (claim/extend/share/delete) */
  showLifecycle?: boolean;
}

export default function EnvironmentPicker({ workspaceCode, value, onChange, readOnly, userEmail, showLifecycle }: EnvironmentPickerProps) {
  const [environments, setEnvironments] = useState<MissionControlEnvironment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [claimOpen, setClaimOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState<MissionControlEnvironment | null>(null);
  const [shareTarget, setShareTarget] = useState<MissionControlEnvironment | null>(null);

  const loadEnvironments = useCallback(async () => {
    if (!workspaceCode || !userEmail) return;
    setLoading(true);
    setError('');
    try {
      const envs = await fetchEnvironmentsByWorkspace(workspaceCode, userEmail);
      setEnvironments(envs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load environments');
    } finally {
      setLoading(false);
    }
  }, [workspaceCode, userEmail]);

  useEffect(() => { loadEnvironments(); }, [loadEnvironments]);

  function getAccessLevel(envId: string): AccessLevel {
    const cfg = value.find((c) => c.environmentId === envId);
    return cfg?.accessLevel ?? 'none';
  }

  function setAccessLevel(env: MissionControlEnvironment, level: AccessLevel) {
    const existing = value.filter((c) => c.environmentId !== env.EnvironmentId);
    if (level === 'none') {
      onChange(existing);
    } else {
      onChange([...existing, {
        environmentId: env.EnvironmentId,
        instanceUrl: env.InstanceUrl,
        friendlyName: env.FriendlyName,
        type: env.Type,
        accessLevel: level,
        expiresOn: env.ExpiresOn,
      }]);
    }
  }

  function getAccessOptions(type: EnvironmentType): IDropdownOption[] {
    const opts: IDropdownOption[] = [
      { key: 'none', text: ACCESS_LEVEL_LABELS.none },
      { key: 'read', text: ACCESS_LEVEL_LABELS.read },
    ];
    if (canWrite(type)) {
      opts.push({ key: 'readwrite', text: ACCESS_LEVEL_LABELS.readwrite });
    } else {
      opts.push({ key: 'readwrite', text: `${ACCESS_LEVEL_LABELS.readwrite} 🔒`, disabled: true });
    }
    return opts;
  }

  function handleDeleteDevBox(env: MissionControlEnvironment) {
    if (!window.confirm(`Delete DevBox "${env.FriendlyName}"? This cannot be undone.`)) return;
    deleteDevBoxApi(env.EnvironmentId).then(() => {
      loadEnvironments();
    }).catch((err) => {
      setError(err instanceof Error ? err.message : 'Failed to delete DevBox');
    });
  }

  if (!workspaceCode) {
    return (
      <div className={styles.emptyState}>
        <Text variant="small">Select a workspace first to discover environments.</Text>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <Spinner size={SpinnerSize.medium} label="Loading environments…" />
      </div>
    );
  }

  const grouped = groupEnvironmentsByType(environments);

  return (
    <div className={styles.envPicker}>
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')} className={styles.errorState}>
          {error}
        </MessageBar>
      )}

      {showLifecycle && (
        <div className={styles.claimBar}>
          <Text variant="small" styles={{ root: { flex: 1 } }}>Need a dev environment?</Text>
          <ActionButton iconProps={{ iconName: 'Add' }} text="Claim DevBox" onClick={() => setClaimOpen(true)} />
        </div>
      )}

      {ENV_TYPE_ORDER.map((type) => {
        const envs = grouped[type];
        if (envs.length === 0) return null;
        return (
          <div key={type} className={styles.typeSection}>
            <div className={styles.typeHeader}>
              <span className={styles.typeBadge} data-type={type}>{type}</span>
              <span className={styles.typeCount}>{envs.length} environment{envs.length !== 1 ? 's' : ''}</span>
            </div>
            {envs.map((env) => (
              <EnvironmentRow
                key={env.EnvironmentId}
                env={env}
                accessLevel={getAccessLevel(env.EnvironmentId)}
                accessOptions={getAccessOptions(env.Type)}
                onAccessChange={(level) => setAccessLevel(env, level)}
                readOnly={readOnly}
                showLifecycle={showLifecycle && env.Type === 'DevBox'}
                onExtend={() => setExtendTarget(env)}
                onShare={() => setShareTarget(env)}
                onDelete={() => handleDeleteDevBox(env)}
              />
            ))}
          </div>
        );
      })}

      {environments.length === 0 && !error && (
        <div className={styles.emptyState}>
          <Text variant="small">No environments found for this workspace.</Text>
          {showLifecycle && (
            <ActionButton iconProps={{ iconName: 'Add' }} text="Claim a DevBox" onClick={() => setClaimOpen(true)} />
          )}
        </div>
      )}

      {claimOpen && (
        <ClaimDevBoxDialog
          workspaceCode={workspaceCode}
          userEmail={userEmail}
          onDismiss={() => setClaimOpen(false)}
          onClaimed={() => { setClaimOpen(false); loadEnvironments(); }}
        />
      )}

      {extendTarget && (
        <ExtendDevBoxDialog
          env={extendTarget}
          onDismiss={() => setExtendTarget(null)}
          onExtended={() => { setExtendTarget(null); loadEnvironments(); }}
        />
      )}

      {shareTarget && (
        <ShareDevBoxDialog
          env={shareTarget}
          onDismiss={() => setShareTarget(null)}
          onShared={() => setShareTarget(null)}
        />
      )}
    </div>
  );
}

function EnvironmentRow({
  env, accessLevel, accessOptions, onAccessChange, readOnly, showLifecycle, onExtend, onShare, onDelete,
}: {
  env: MissionControlEnvironment;
  accessLevel: AccessLevel;
  accessOptions: IDropdownOption[];
  onAccessChange: (level: AccessLevel) => void;
  readOnly?: boolean;
  showLifecycle?: boolean;
  onExtend: () => void;
  onShare: () => void;
  onDelete: () => void;
}) {
  const expiryInfo = formatExpiry(env.ExpiresOn);

  return (
    <div className={`${styles.envRow} ${readOnly ? styles.readOnly : ''}`}>
      <div className={styles.envInfo}>
        <span className={styles.envName}>{env.FriendlyName}</span>
        <span className={styles.envUrl}>{env.InstanceUrl}</span>
        {env.ExpiresOn && (
          <span className={`${styles.envExpiry} ${expiryInfo.className}`}>{expiryInfo.text}</span>
        )}
      </div>

      {readOnly ? (
        <span className={styles.accessReadOnly} data-level={accessLevel}>
          {ACCESS_LEVEL_LABELS[accessLevel]}
          {accessLevel === 'none' && env.Type !== 'PROD' && null}
          {env.Type === 'PROD' && accessLevel !== 'none' && (
            <TooltipHost content="Write access is not available for production environments">
              <span className={styles.lockIcon}>🔒</span>
            </TooltipHost>
          )}
        </span>
      ) : (
        <Dropdown
          className={styles.accessSelector}
          options={accessOptions}
          selectedKey={accessLevel}
          onChange={(_, opt) => opt && onAccessChange(opt.key as AccessLevel)}
          styles={{ root: { minWidth: 140 }, dropdown: { minWidth: 140 } }}
        />
      )}

      {showLifecycle && !readOnly && (
        <div className={styles.actions}>
          <TooltipHost content="Extend expiration">
            <IconButton iconProps={{ iconName: 'Clock' }} onClick={onExtend} />
          </TooltipHost>
          <TooltipHost content="Share with user">
            <IconButton iconProps={{ iconName: 'Share' }} onClick={onShare} />
          </TooltipHost>
          <TooltipHost content="Delete DevBox">
            <IconButton iconProps={{ iconName: 'Delete' }} onClick={onDelete} styles={{ root: { color: 'var(--ab-error)' } }} />
          </TooltipHost>
        </div>
      )}
    </div>
  );
}

function formatExpiry(expiresOn: string | null): { text: string; className: string } {
  if (!expiresOn) return { text: '', className: '' };
  const diff = new Date(expiresOn).getTime() - Date.now();
  if (diff <= 0) return { text: 'Expired', className: 'expired' };
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  if (days === 0) return { text: `Expires in ${hours}h`, className: 'expiringSoon' };
  if (days <= 2) return { text: `Expires in ${days}d ${hours}h`, className: 'expiringSoon' };
  return { text: `Expires in ${days} days`, className: '' };
}
